import React from 'react';
import { graphql } from 'react-relay';

import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

import { QueryInterop as Query } from '@confluence/apollo-relay-interop/entry-points/QueryInterop';
import { PageSubscriber } from '@confluence/page-context';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { expValEquals } from '@confluence/feature-experiments';

import { CustomHeaderFooterComponent } from './CustomHeaderFooterComponent';
import type {
	CustomHeaderFooterQuery$data,
	CustomHeaderFooterQuery$variables,
} from './__generated__/CustomHeaderFooterQuery.graphql';

const CustomHeaderFooterQuery = graphql`
	query CustomHeaderFooterQuery($spaceKey: String) {
		space(key: $spaceKey) {
			id
			settings {
				customHeaderAndFooter {
					header {
						html
						js
						css
						spaUnfriendlyMacros {
							name
						}
					}
					footer {
						html
						js
						css
						spaUnfriendlyMacros {
							name
						}
					}
				}
			}
		}
	}
`;

type CustomHeaderFooterProps = {
	type: 'header' | 'footer';
};

export function CustomHeaderFooter({ type }: CustomHeaderFooterProps) {
	return (
		<ErrorBoundary attribution={Attribution.BACKBONE}>
			<PageSubscriber>
				{({ spaceKey, contentId }) => (
					<Query<CustomHeaderFooterQuery$data, CustomHeaderFooterQuery$variables>
						query={CustomHeaderFooterQuery}
						variables={{ spaceKey }}
					>
						{({ loading, error, data }) => {
							const settings: any = data?.space?.settings?.customHeaderAndFooter?.[type];

							if (loading || error || !settings?.html) {
								return null;
							}

							return (
								<>
									<UFOLoadHold
										name="custom-header-footer-contents"
										hold={
											expValEquals(
												'ufo-experimental-custom-header-footer-confluence',
												'isEnabled',
												true,
											) && loading
										}
									/>
									<CustomHeaderFooterComponent
										spaUnfriendlyMacros={settings.spaUnfriendlyMacros}
										type={type}
										css={settings.css}
										js={settings.js}
										html={settings.html}
										key={contentId}
									/>
								</>
							);
						}}
					</Query>
				)}
			</PageSubscriber>
		</ErrorBoundary>
	);
}
